import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { cleanLocation, clearConsent, getConsent, isPrerender, setConsent, setPageLocation, trackPageView } from "@/lib/analytics";

/**
 * RouteTracker — SPA page_view on every route change.
 * gtag is configured with send_page_view:false in index.html, so this is the
 * only source of page views. Admin screens are internal traffic and skipped.
 */
export function RouteTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (isPrerender() || pathname.startsWith("/admin")) return;
    setPageLocation();
    const page_location = cleanLocation();
    // Pages set <title> via react-helmet-async, often only after their data
    // loads, and it passes through the default title on the way. Send once the
    // title has settled on a new value, after 2s at most, or right away if the
    // visitor leaves first (so quick clicks still count as a view).
    const before = document.title;
    let done = false;
    let settle;
    const send = () => {
      if (done) return;
      done = true;
      observer.disconnect();
      clearTimeout(settle);
      clearTimeout(fallback);
      trackPageView(page_location);
    };
    const observer = new MutationObserver(() => {
      if (document.title === before) return;
      clearTimeout(settle);
      settle = setTimeout(send, 300);
    });
    observer.observe(document.head, { childList: true, subtree: true, characterData: true });
    const fallback = setTimeout(send, 2000);
    return send;
  }, [pathname]);
  return null;
}

/**
 * ConsentBanner — Consent Mode v2. Until the visitor chooses, Google gets
 * only cookieless pings (no identifiers stored on the device).
 */
export function ConsentBanner() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Build-time snapshot: the banner must not be baked into the static HTML.
    if (isPrerender() || getConsent()) return;
    // Honour Do Not Track / Global Privacy Control as "Only essential".
    const optOut = navigator.doNotTrack === "1" || window.doNotTrack === "1" || navigator.globalPrivacyControl === true;
    if (optOut) setConsent(false);
    else setOpen(true);
  }, []);

  if (!open || isPrerender() || pathname.startsWith("/admin")) return null;

  const choose = (granted) => {
    setConsent(granted);
    setOpen(false);
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie preferences"
      data-testid="consent-banner"
      className="fixed inset-x-4 bottom-4 z-[60] mx-auto max-w-xl rounded-2xl border border-brand-gold/30 bg-brand-obsidian/95 p-5 text-brand-bone shadow-2xl backdrop-blur"
    >
      <p className="font-body text-sm leading-relaxed">
        We use Google Analytics cookies to understand which pages help families find the right oil, so we can make this site better.
        Nothing is sold. Read our <Link to="/cookie-policy" className="underline hover:text-brand-gold">cookie policy</Link>.
      </p>
      <div className="mt-4 flex flex-wrap gap-3">
        <Button variant="primary" size="sm" onClick={() => choose(true)} data-testid="consent-accept">
          Accept
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => choose(false)}
          className="!text-brand-bone hover:!text-brand-gold"
          data-testid="consent-reject"
        >
          Only essential
        </Button>
      </div>
    </div>
  );
}

/** Clears the stored choice and reloads, so the banner asks again. */
export function ConsentResetButton() {
  const reset = () => {
    clearConsent();
    window.location.reload();
  };
  return (
    <button type="button" onClick={reset} className="underline hover:text-brand-gold" data-testid="consent-reset">
      reopen cookie choices.
    </button>
  );
}
